/**
 * StorefrontSettingsPage
 *
 * Vitrine: cores do tema + sessões/trilhos da home.
 * Hero/banners ficam em /erp/configuracoes/hero.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Loader2,
  RotateCcw,
  AlertTriangle,
  Plus,
  Trash2,
  GripVertical,
  LayoutList,
  Palette,
  LayoutTemplate,
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { erpSettingsService } from '../../../services/erpSettingsService'
import { groupService } from '../../../services/api'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'
import './StorefrontSettingsPage.css'

const MAX_SHELVES = 12

const DEFAULT_THEME = {
  page_bg: '#f0f4f8',
  header_bg: '#ffffff',
  card_bg: '#ffffff',
  cart_bg: '#37971c',
  see_all: '#37971c',
  add_btn: '#37971c',
}

function cloneTheme(theme = {}) {
  return {
    page_bg: theme.page_bg || DEFAULT_THEME.page_bg,
    header_bg: theme.header_bg || DEFAULT_THEME.header_bg,
    card_bg: theme.card_bg || DEFAULT_THEME.card_bg,
    cart_bg: theme.cart_bg || DEFAULT_THEME.cart_bg,
    see_all: theme.see_all || DEFAULT_THEME.see_all,
    add_btn: theme.add_btn || DEFAULT_THEME.add_btn,
  }
}

function newShelfId() {
  return `shelf-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function cloneShelves(list = []) {
  return (list || []).map((item, index) => ({
    id: item.id || newShelfId(),
    title: item.title || '',
    subtitle: item.subtitle || '',
    group_ids: Array.isArray(item.group_ids)
      ? item.group_ids.map(Number).filter((n) => Number.isInteger(n) && n > 0)
      : [],
    sort_order: Number.isFinite(Number(item.sort_order)) ? Number(item.sort_order) : index,
    active: item.active !== false,
  }))
}

function emptyForm() {
  return { home_shelves: [], theme: cloneTheme() }
}

function cloneForm(form) {
  return {
    home_shelves: cloneShelves(form?.home_shelves),
    theme: cloneTheme(form?.theme),
  }
}

function formsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Lista plana pai + filhos para o multi-select */
function flattenGroups(groups = []) {
  const rows = []
  for (const g of groups) {
    rows.push({
      id: g.id,
      name: g.name,
      depth: 0,
    })
    if (Array.isArray(g.children)) {
      for (const child of g.children) {
        rows.push({
          id: child.id,
          name: child.name,
          depth: 1,
          parentName: g.name,
        })
      }
    }
  }
  return rows
}

function StorefrontSettingsPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const canEdit = hasPermission(MODULES.SETTINGS, ACTIONS.UPDATE)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])
  const [savedForm, setSavedForm] = useState(emptyForm())
  const [form, setForm] = useState(emptyForm())
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null })
  const [groupOptions, setGroupOptions] = useState([])

  const isDirty = useMemo(() => !formsEqual(form, savedForm), [form, savedForm])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [settingsRes, groupsRes] = await Promise.all([
        erpSettingsService.getNamespace('storefront'),
        groupService.getPublicGroups().catch(() => ({ success: false, data: [] })),
      ])

      if (!settingsRes?.success) {
        throw new Error(settingsRes?.message || 'Falha ao carregar vitrine')
      }

      const settings = settingsRes.data?.settings || {}
      const next = cloneForm({
        home_shelves: settings.home_shelves,
        theme: settings.theme,
      })
      setSavedForm(next)
      setForm(next)
      setMeta({
        updated_at: settingsRes.data?.updated_at || null,
        updated_by: settingsRes.data?.updated_by || null,
      })

      const groups = groupsRes?.success && Array.isArray(groupsRes.data) ? groupsRes.data : []
      setGroupOptions(flattenGroups(groups))
    } catch (err) {
      console.error(err)
      setError('Não foi possível carregar as configurações da vitrine.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateShelf = (index, patch) => {
    setForm((prev) => {
      const next = cloneForm(prev)
      next.home_shelves[index] = { ...next.home_shelves[index], ...patch }
      return next
    })
  }

  const toggleShelfGroup = (shelfIndex, groupId) => {
    setForm((prev) => {
      const next = cloneForm(prev)
      const shelf = next.home_shelves[shelfIndex]
      const id = Number(groupId)
      const has = shelf.group_ids.includes(id)
      shelf.group_ids = has
        ? shelf.group_ids.filter((g) => g !== id)
        : [...shelf.group_ids, id]
      return next
    })
  }

  const updateTheme = (patch) => {
    setForm((prev) => {
      const next = cloneForm(prev)
      next.theme = { ...next.theme, ...patch }
      return next
    })
  }

  const addShelf = () => {
    if (form.home_shelves.length >= MAX_SHELVES) {
      showToast(`Máximo de ${MAX_SHELVES} sessões.`, 'warning')
      return
    }
    setForm((prev) => {
      const next = cloneForm(prev)
      next.home_shelves.push({
        id: newShelfId(),
        title: '',
        subtitle: '',
        group_ids: [],
        sort_order: next.home_shelves.length,
        active: true,
      })
      return next
    })
  }

  const removeShelf = (index) => {
    setForm((prev) => {
      const next = cloneForm(prev)
      next.home_shelves = next.home_shelves
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, sort_order: i }))
      return next
    })
  }

  const moveShelf = (index, direction) => {
    setForm((prev) => {
      const next = cloneForm(prev)
      const target = index + direction
      if (target < 0 || target >= next.home_shelves.length) return prev
      const tmp = next.home_shelves[index]
      next.home_shelves[index] = next.home_shelves[target]
      next.home_shelves[target] = tmp
      next.home_shelves = next.home_shelves.map((item, i) => ({ ...item, sort_order: i }))
      return next
    })
  }

  const validate = () => {
    const errors = []
    form.home_shelves.forEach((shelf, index) => {
      if (!shelf.title?.trim()) {
        errors.push(`Sessão ${index + 1}: informe o título.`)
      }
    })
    setValidationErrors(errors)
    return errors.length === 0
  }

  const handleSave = async () => {
    if (!canEdit) return
    if (!validate()) return

    setSaving(true)
    try {
      const payload = {
        home_shelves: form.home_shelves.map((item, index) => ({
          id: item.id,
          title: item.title.trim(),
          subtitle: item.subtitle?.trim() || '',
          group_ids: item.group_ids,
          sort_order: index,
          active: item.active !== false,
        })),
        theme: cloneTheme(form.theme),
      }
      const response = await erpSettingsService.updateNamespace('storefront', payload)
      if (!response?.success) {
        throw new Error(response?.message || 'Falha ao salvar')
      }
      const settings = response.data?.settings || payload
      const next = cloneForm({
        home_shelves: settings.home_shelves || payload.home_shelves,
        theme: settings.theme || payload.theme,
      })
      setSavedForm(next)
      setForm(next)
      setMeta({
        updated_at: response.data?.updated_at || null,
        updated_by: response.data?.updated_by || null,
      })
      setValidationErrors([])
      showToast('Vitrine salva com sucesso.', 'success')
    } catch (err) {
      console.error(err)
      const details = err?.response?.data?.details
      if (Array.isArray(details) && details.length) {
        setValidationErrors(details)
      }
      showToast(err?.response?.data?.message || 'Erro ao salvar vitrine.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setForm(cloneForm(savedForm))
    setValidationErrors([])
  }

  if (loading) {
    return (
      <div className="erp-settings-page cockpit-settings-page">
        <div className="erp-settings-loading">
          <Loader2 className="cockpit-settings-spinner" size={28} />
          <p>Carregando vitrine...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="erp-settings-page cockpit-settings-page storefront-settings-page">
      <button
        type="button"
        className="cockpit-settings-back-btn"
        onClick={() => navigate('/erp/configuracoes')}
      >
        <ArrowLeft size={14} />
        Configurações
      </button>

      <header className="erp-settings-header">
        <div className="erp-settings-header-main">
          <div className="cockpit-settings-icon-badge" style={{ background: '#0284c7' }}>
            <Palette size={22} />
          </div>
          <div>
            <h1 className="erp-settings-title">Vitrine</h1>
            <p className="erp-settings-subtitle">
              Cores da loja pública e sessões (trilhos) da home.
            </p>
            {meta.updated_at && (
              <p className="cockpit-settings-meta">
                Atualizado em {new Date(meta.updated_at).toLocaleString('pt-BR')}
                {meta.updated_by?.name ? ` por ${meta.updated_by.name}` : ''}
              </p>
            )}
          </div>
        </div>
      </header>

      {!canEdit && (
        <div className="cockpit-settings-readonly-banner">
          Você pode visualizar, mas não tem permissão para editar estas configurações.
        </div>
      )}

      {error && (
        <div className="erp-settings-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="erp-settings-error cockpit-settings-validation-list">
          <AlertTriangle size={18} />
          <ul>
            {validationErrors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* —— Cores —— */}
      <section className="storefront-settings-section">
        <div className="storefront-settings-section-head">
          <h2>
            <Palette size={18} />
            Cores da vitrine
          </h2>
          <p>Fundo, header, cards, carrinho, “Ver tudo” e botão + da loja pública.</p>
        </div>

        <div className="storefront-theme-grid">
          {[
            {
              key: 'page_bg',
              label: 'Fundo da página',
              hint: 'Background geral da home e listagens',
            },
            {
              key: 'header_bg',
              label: 'Header',
              hint: 'Barra superior (mobile e desktop)',
            },
            {
              key: 'card_bg',
              label: 'Cards de produto',
              hint: 'Fundo dos cards nos trilhos e grade',
            },
            {
              key: 'cart_bg',
              label: 'Carrinho',
              hint: 'Botão do carrinho no header e destaques do drawer',
            },
            {
              key: 'see_all',
              label: 'Ver tudo ›',
              hint: 'Link “Ver tudo” nos trilhos da home',
            },
            {
              key: 'add_btn',
              label: 'Botão +',
              hint: 'Adicionar produto no card (+)',
            },
          ].map((field) => (
            <label key={field.key} className="storefront-theme-field">
              <span className="storefront-theme-label">{field.label}</span>
              <span className="storefront-theme-hint">{field.hint}</span>
              <div className="storefront-theme-controls">
                <input
                  type="color"
                  value={
                    /^#[0-9a-fA-F]{6}$/.test(form.theme[field.key])
                      ? form.theme[field.key]
                      : DEFAULT_THEME[field.key]
                  }
                  disabled={!canEdit || saving}
                  onChange={(e) => updateTheme({ [field.key]: e.target.value })}
                  aria-label={field.label}
                />
                <input
                  type="text"
                  className="storefront-theme-hex"
                  value={form.theme[field.key]}
                  disabled={!canEdit || saving}
                  maxLength={64}
                  spellCheck={false}
                  onChange={(e) => updateTheme({ [field.key]: e.target.value })}
                />
                <button
                  type="button"
                  className="storefront-theme-reset"
                  disabled={!canEdit || saving}
                  onClick={() => updateTheme({ [field.key]: DEFAULT_THEME[field.key] })}
                >
                  Padrão
                </button>
              </div>
              <div
                className="storefront-theme-preview"
                style={{ background: form.theme[field.key] }}
                aria-hidden
              />
            </label>
          ))}
        </div>
      </section>

      {/* —— Hero / Banners (link) —— */}
      <section className="storefront-settings-section">
        <div className="storefront-settings-section-head">
          <h2><LayoutTemplate size={18} /> Hero / Banners</h2>
          <p>O carrossel da home agora é configurado em uma página dedicada.</p>
        </div>
        <button type="button" className="storefront-settings-add-btn" onClick={() => navigate('/erp/configuracoes/hero')}>
          <LayoutTemplate size={16} />
          Abrir configuração do Hero
        </button>
      </section>

      {/* —— Sessões / trilhos —— */}
      <section className="storefront-settings-section">
        <div className="storefront-settings-section-head">
          <h2>
            <LayoutList size={18} />
            Sessões da home (mobile)
          </h2>
          <p>
            Título, subtítulo e um ou mais grupos de produtos. Sem grupos, a sessão mostra
            produtos em destaque.
          </p>
        </div>

        <div className="storefront-settings-list">
          {form.home_shelves.length === 0 && (
            <div className="storefront-settings-empty">
              <p>Nenhuma sessão cadastrada.</p>
              <p>
                Enquanto estiver vazio, a loja usa as sessões padrão do app. Cadastre aqui para
                controlar a vitrine.
              </p>
            </div>
          )}

          {form.home_shelves.map((shelf, index) => (
            <article key={shelf.id} className="storefront-settings-card">
              <div className="storefront-settings-card-toolbar">
                <span className="storefront-settings-order">
                  <GripVertical size={14} />
                  Sessão #{index + 1}
                </span>
                <div className="storefront-settings-card-actions">
                  <button
                    type="button"
                    className="storefront-settings-icon-btn"
                    disabled={!canEdit || index === 0}
                    onClick={() => moveShelf(index, -1)}
                    aria-label="Mover sessão para cima"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="storefront-settings-icon-btn"
                    disabled={!canEdit || index === form.home_shelves.length - 1}
                    onClick={() => moveShelf(index, 1)}
                    aria-label="Mover sessão para baixo"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="storefront-settings-icon-btn storefront-settings-icon-btn--danger"
                    disabled={!canEdit}
                    onClick={() => removeShelf(index)}
                    aria-label="Remover sessão"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="storefront-settings-shelf-body">
                <label className="storefront-settings-label">
                  Título
                  <input
                    type="text"
                    value={shelf.title}
                    disabled={!canEdit}
                    onChange={(e) => updateShelf(index, { title: e.target.value })}
                    placeholder="Ex.: Cadeiras de Rodas & Ortopedia"
                    maxLength={120}
                  />
                </label>

                <label className="storefront-settings-label">
                  Subtítulo
                  <input
                    type="text"
                    value={shelf.subtitle || ''}
                    disabled={!canEdit}
                    onChange={(e) => updateShelf(index, { subtitle: e.target.value })}
                    placeholder="Texto de apoio (opcional)"
                    maxLength={240}
                  />
                </label>

                <div className="storefront-settings-upload-row">
                  <label className="storefront-settings-active">
                    <input
                      type="checkbox"
                      checked={shelf.active !== false}
                      disabled={!canEdit}
                      onChange={(e) => updateShelf(index, { active: e.target.checked })}
                    />
                    Ativa na home
                  </label>
                  <span className="storefront-settings-groups-hint">
                    {shelf.group_ids.length
                      ? `${shelf.group_ids.length} grupo(s) selecionado(s)`
                      : 'Sem grupos → usa destaques'}
                  </span>
                </div>

                <fieldset className="storefront-settings-groups" disabled={!canEdit}>
                  <legend>Grupos de produtos (opcional, múltiplos)</legend>
                  {groupOptions.length === 0 ? (
                    <p className="storefront-settings-groups-empty">
                      Nenhum grupo ativo encontrado.
                    </p>
                  ) : (
                    <div className="storefront-settings-groups-grid">
                      {groupOptions.map((g) => (
                        <label
                          key={g.id}
                          className={`storefront-settings-group-chip${
                            g.depth ? ' storefront-settings-group-chip--child' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={shelf.group_ids.includes(g.id)}
                            onChange={() => toggleShelfGroup(index, g.id)}
                          />
                          <span>
                            {g.depth ? `${g.parentName} › ` : ''}
                            {g.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
              </div>
            </article>
          ))}
        </div>

        <button
          type="button"
          className="storefront-settings-add-btn storefront-settings-add-btn--inline"
          disabled={!canEdit || form.home_shelves.length >= MAX_SHELVES}
          onClick={addShelf}
        >
          <Plus size={16} />
          Adicionar sessão
        </button>
      </section>

      <div className="storefront-settings-footer-bar">
        <div className="storefront-settings-save-group">
          <button
            type="button"
            className="cockpit-settings-btn-secondary"
            disabled={!canEdit || !isDirty || saving}
            onClick={handleReset}
          >
            <RotateCcw size={16} />
            Desfazer
          </button>
          <button
            type="button"
            className="cockpit-settings-btn-primary"
            disabled={!canEdit || !isDirty || saving}
            onClick={handleSave}
          >
            {saving ? <Loader2 size={16} className="cockpit-settings-spinner" /> : <Save size={16} />}
            Salvar
          </button>
        </div>
      </div>

      <Toast toast={toast} onClose={hideToast} />
    </div>
  )
}

export default StorefrontSettingsPage
